/**
 * PORequestForm.tsx — Create / edit PO form  (Phase 66 — redesigned)
 *
 * Routes:
 *   /purchase-orders/new           → create mode
 *   /purchase-orders/:id/edit      → edit mode (DRAFT only)
 *
 * Accessible by: ADMIN, POWER_USER, PO_REQUESTOR
 *
 * Inline CSSProperties — matches InvoiceHeader / GLLineItems design system.
 */

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createPO, updatePO, getPO } from '../api/poApi';
import { getGLAccounts, getJobs, searchVendors } from '../api';
import type { GLAccount, Job } from '../types';
import type { CreatePORequest, POLineItemInput, POEntryMode, POType } from '../types/poTypes';
import { PAYMENT_TERMS_OPTIONS } from '../types/poTypes';
import { useAuth } from '../context/AuthContext';

// ── Style constants (matches InvoiceHeader / GLLineItems) ────────────────────

const NAVY = '#00386C';
const RED = '#E33126';
const LABEL_GRAY = '#718096';
const BORDER = '#cbd5e0';
const BG_LIGHT = '#f7fafc';

const cardStyle: React.CSSProperties = {
  background: '#fff',
  border: `1px solid ${BORDER}`,
  borderRadius: 8,
  padding: '20px 24px',
  marginBottom: 20,
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 11,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: LABEL_GRAY,
  marginBottom: 4,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  border: `1px solid ${BORDER}`,
  borderRadius: 4,
  padding: '7px 10px',
  fontSize: 13,
  outline: 'none',
  boxSizing: 'border-box',
};

const selectStyle: React.CSSProperties = { ...inputStyle, appearance: 'auto' as any };

const sectionTitleStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 700,
  color: NAVY,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  marginBottom: 14,
  borderBottom: `2px solid ${NAVY}`,
  paddingBottom: 6,
};

// ── Types ────────────────────────────────────────────────────────────────────

interface LineItemDraft extends POLineItemInput {
  _key: string;
}

function emptyLine(): LineItemDraft {
  return {
    _key: Math.random().toString(36).slice(2),
    description: '',
    gl_account: '',
    gl_description: '',
    job_number: '',
    quantity: 1,
    unit_price: 0,
    entry_mode: 'CALCULATED',
    notes: '',
  };
}

function fmtCurrency(n: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}

function lineAmount(line: LineItemDraft): number {
  if (line.entry_mode === 'FLAT_TOTAL') return Number(line.unit_price) || 0;
  return (Number(line.quantity) || 0) * (Number(line.unit_price) || 0);
}

// ── Component ────────────────────────────────────────────────────────────────

const PORequestForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const { user } = useAuth();
  const isEditMode = Boolean(id);

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEditMode);
  const [error, setError] = useState<string | null>(null);

  // Header fields
  const [vendorName, setVendorName] = useState('');
  const [vendorNumber, setVendorNumber] = useState('');
  const [vendorId, setVendorId] = useState<number | null>(null);
  const [jobNumber, setJobNumber] = useState('');
  const [jobName, setJobName] = useState('');
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [notesVendor, setNotesVendor] = useState('');
  const [approverEmail, setApproverEmail] = useState('');
  const [poDate, setPoDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [requiredByDate, setRequiredByDate] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [poType, setPoType] = useState<POType>('SPOT_BUY');

  // Ship-to
  const [shipAddress, setShipAddress] = useState('');
  const [shipCity, setShipCity] = useState('');
  const [shipState, setShipState] = useState('');
  const [shipZip, setShipZip] = useState('');
  const [shipContactName, setShipContactName] = useState('');
  const [shipContactPhone, setShipContactPhone] = useState('');

  // Line items
  const [lines, setLines] = useState<LineItemDraft[]>([emptyLine()]);

  // Lookup data
  const [glAccounts, setGlAccounts] = useState<GLAccount[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);

  // Vendor typeahead
  const [vendorQuery, setVendorQuery] = useState('');
  const [vendorResults, setVendorResults] = useState<{ vendor_number: number; vendor_name: string }[]>([]);
  const [vendorDropdownOpen, setVendorDropdownOpen] = useState(false);
  const vendorRef = useRef<HTMLDivElement>(null);

  // ── Load lookup data ───────────────────────────────────────────────────────

  useEffect(() => {
    getGLAccounts().then(setGlAccounts).catch(() => {});
    getJobs().then(setJobs).catch(() => {});
  }, []);

  // ── Vendor search (debounced) ──────────────────────────────────────────────

  useEffect(() => {
    if (!vendorQuery.trim()) { setVendorResults([]); setVendorDropdownOpen(false); return; }
    const timer = setTimeout(async () => {
      try {
        const results = await searchVendors(vendorQuery);
        setVendorResults(results);
        setVendorDropdownOpen(results.length > 0);
      } catch { /* ignore */ }
    }, 250);
    return () => clearTimeout(timer);
  }, [vendorQuery]);

  // Close vendor dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (vendorRef.current && !vendorRef.current.contains(e.target as Node))
        setVendorDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  function selectVendor(v: { vendor_number: number; vendor_name: string }) {
    setVendorName(v.vendor_name);
    setVendorNumber(String(v.vendor_number));
    setVendorId(v.vendor_number);
    setVendorQuery('');
    setVendorDropdownOpen(false);
  }

  // ── Job selection → auto-fill ship-to ──────────────────────────────────────

  const handleJobChange = useCallback((val: string) => {
    setJobNumber(val);
    const job = jobs.find(j => j.job_number === val);
    if (job) {
      setJobName(job.job_name || '');
      setShipAddress(job.job_address || '');
      setShipCity(job.job_city || '');
      setShipState(job.job_state || '');
      setShipZip(job.job_zip || '');
    } else {
      setJobName('');
    }
  }, [jobs]);

  // ── Load existing PO in edit mode ──────────────────────────────────────────

  useEffect(() => {
    if (!isEditMode || !id) return;
    setLoading(true);
    getPO(parseInt(id, 10))
      .then(po => {
        setVendorName(po.vendor_name);
        setVendorNumber(po.vendor_number || '');
        setVendorId(po.vendor_id ?? null);
        setJobNumber(po.job_number);
        setJobName(po.job_name || '');
        setDescription(po.description || '');
        setNotes(po.notes || '');
        setNotesVendor(po.notes_vendor || '');
        setApproverEmail(po.approver_email || '');
        setPoDate(po.po_date || new Date().toISOString().slice(0, 10));
        setRequiredByDate(po.required_by_date || '');
        setPaymentTerms(po.payment_terms || '');
        setPoType(po.po_type || 'SPOT_BUY');
        setShipAddress(po.ship_to_address || '');
        setShipCity(po.ship_to_city || '');
        setShipState(po.ship_to_state || '');
        setShipZip(po.ship_to_zip || '');
        setShipContactName(po.ship_to_contact_name || '');
        setShipContactPhone(po.ship_to_contact_phone || '');
        if (po.lines && po.lines.length > 0) {
          setLines(po.lines.map(l => ({
            _key: String(l.id),
            description: l.description,
            gl_account: l.gl_account || '',
            gl_description: l.gl_description || '',
            job_number: l.job_number || '',
            quantity: l.quantity,
            unit_price: l.unit_price,
            entry_mode: l.entry_mode || 'CALCULATED',
            notes: l.notes || '',
          })));
        }
      })
      .catch(() => setError('Failed to load PO for editing'))
      .finally(() => setLoading(false));
  }, [id, isEditMode]);

  // ── Line helpers ───────────────────────────────────────────────────────────

  function updateLine(key: string, field: keyof LineItemDraft, value: string | number) {
    setLines(prev => prev.map(l => (l._key === key ? { ...l, [field]: value } : l)));
  }

  function toggleEntryMode(key: string) {
    setLines(prev => prev.map(l => {
      if (l._key !== key) return l;
      const newMode: POEntryMode = l.entry_mode === 'CALCULATED' ? 'FLAT_TOTAL' : 'CALCULATED';
      return { ...l, entry_mode: newMode, quantity: newMode === 'FLAT_TOTAL' ? 1 : l.quantity };
    }));
  }

  function addLine() { setLines(prev => [...prev, emptyLine()]); }
  function removeLine(key: string) { setLines(prev => prev.length > 1 ? prev.filter(l => l._key !== key) : prev); }

  const grandTotal = lines.reduce((sum, l) => sum + lineAmount(l), 0);

  // ── Submit ─────────────────────────────────────────────────────────────────

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!vendorName.trim()) { setError('Vendor name is required'); return; }
    if (!jobNumber.trim()) { setError('Job number is required'); return; }
    if (!approverEmail.trim()) { setError('Approver email is required'); return; }
    if (lines.some(l => !l.description.trim())) { setError('All line items must have a description'); return; }

    setSaving(true);
    try {
      const payload: CreatePORequest = {
        vendor_name: vendorName.trim(),
        vendor_number: vendorNumber.trim() || undefined,
        vendor_id: vendorId ?? undefined,
        job_number: jobNumber.trim(),
        job_name: jobName.trim() || undefined,
        description: description.trim() || undefined,
        notes: notes.trim() || undefined,
        notes_vendor: notesVendor.trim() || undefined,
        approver_email: approverEmail.trim(),
        po_date: poDate || undefined,
        required_by_date: requiredByDate || undefined,
        payment_terms: paymentTerms || undefined,
        po_type: poType,
        ship_to_address: shipAddress.trim() || undefined,
        ship_to_city: shipCity.trim() || undefined,
        ship_to_state: shipState.trim() || undefined,
        ship_to_zip: shipZip.trim() || undefined,
        ship_to_contact_name: shipContactName.trim() || undefined,
        ship_to_contact_phone: shipContactPhone.trim() || undefined,
        lines: lines.map(l => ({
          description: l.description,
          gl_account: l.gl_account || undefined,
          gl_description: l.gl_description || undefined,
          job_number: l.job_number || undefined,
          quantity: l.entry_mode === 'FLAT_TOTAL' ? 1 : Number(l.quantity),
          unit_price: Number(l.unit_price),
          entry_mode: l.entry_mode || 'CALCULATED',
          notes: l.notes || undefined,
        })),
      };

      if (isEditMode && id) {
        await updatePO(parseInt(id, 10), payload);
        navigate(`/purchase-orders/${id}`);
      } else {
        const res = await createPO(payload);
        navigate(`/purchase-orders/${res.po_id}`);
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to save purchase order');
    } finally {
      setSaving(false);
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', color: LABEL_GRAY, fontSize: 14 }}>
        Loading…
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '0 16px 40px' }}>

      {/* ── Navy header strip ────────────────────────────────────────────── */}
      <div style={{ background: NAVY, borderRadius: '8px 8px 0 0', padding: '16px 24px', marginBottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h1 style={{ color: '#fff', fontSize: 18, fontWeight: 700, margin: 0 }}>
            {isEditMode ? 'Edit Purchase Order' : 'New Purchase Order'}
          </h1>
          {user && (
            <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, marginTop: 3 }}>
              Requestor: {user.display_name} ({user.email})
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => navigate('/purchase-orders')}
          style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.4)', borderRadius: 4, color: '#fff', fontSize: 12, padding: '5px 14px', cursor: 'pointer' }}
        >
          ← Back to List
        </button>
      </div>

      <form onSubmit={handleSubmit}>

        {/* ── PO Header ──────────────────────────────────────────────────── */}
        <div style={{ ...cardStyle, borderTopLeftRadius: 0, borderTopRightRadius: 0, marginTop: 0 }}>
          <div style={sectionTitleStyle}>PO Header</div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 20px' }}>

            {/* Vendor typeahead */}
            <div ref={vendorRef} style={{ position: 'relative' }}>
              <label style={labelStyle}>Vendor <span style={{ color: RED }}>*</span></label>
              <input
                style={inputStyle}
                value={vendorQuery || vendorName}
                onChange={e => { setVendorQuery(e.target.value); setVendorName(e.target.value); setVendorNumber(''); setVendorId(null); }}
                placeholder="Search vendor name or number…"
              />
              {vendorDropdownOpen && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 4, maxHeight: 200, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.12)' }}>
                  {vendorResults.map(v => (
                    <div
                      key={v.vendor_number}
                      onClick={() => selectVendor(v)}
                      style={{ padding: '8px 12px', fontSize: 13, cursor: 'pointer', borderBottom: `1px solid ${BG_LIGHT}` }}
                      onMouseEnter={e => (e.currentTarget.style.background = BG_LIGHT)}
                      onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
                    >
                      <span style={{ fontWeight: 600 }}>{v.vendor_number}</span> — {v.vendor_name}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Vendor # (read-only after typeahead selection) */}
            <div>
              <label style={labelStyle}>Vendor #</label>
              <input style={{ ...inputStyle, background: vendorNumber ? '#f0f4f8' : undefined }} value={vendorNumber} onChange={e => setVendorNumber(e.target.value)} placeholder="Auto-filled from search" />
            </div>

            {/* PO Type toggle */}
            <div>
              <label style={labelStyle}>PO Type</label>
              <div style={{ display: 'flex', gap: 0, border: `1px solid ${BORDER}`, borderRadius: 4, overflow: 'hidden' }}>
                {(['SPOT_BUY', 'BLANKET'] as POType[]).map(t => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setPoType(t)}
                    style={{
                      flex: 1, padding: '7px 0', fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer',
                      background: poType === t ? NAVY : '#fff',
                      color: poType === t ? '#fff' : LABEL_GRAY,
                    }}
                  >
                    {t === 'SPOT_BUY' ? 'Spot Buy' : 'Blanket'}
                  </button>
                ))}
              </div>
            </div>

            {/* Payment Terms */}
            <div>
              <label style={labelStyle}>Payment Terms</label>
              <select style={selectStyle} value={paymentTerms} onChange={e => setPaymentTerms(e.target.value)}>
                <option value="">— Select —</option>
                {PAYMENT_TERMS_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            {/* Job # (dropdown from job list) */}
            <div>
              <label style={labelStyle}>Job # <span style={{ color: RED }}>*</span></label>
              <select style={selectStyle} value={jobNumber} onChange={e => handleJobChange(e.target.value)}>
                <option value="">— Select Job —</option>
                {jobs.map(j => <option key={j.job_number} value={j.job_number}>{j.job_number} — {j.job_name}</option>)}
              </select>
            </div>

            {/* Job Name (read-only auto-fill) */}
            <div>
              <label style={labelStyle}>Job Name</label>
              <input style={{ ...inputStyle, background: '#f0f4f8' }} value={jobName} readOnly tabIndex={-1} />
            </div>

            {/* PO Date */}
            <div>
              <label style={labelStyle}>PO Date</label>
              <input type="date" style={inputStyle} value={poDate} onChange={e => setPoDate(e.target.value)} />
            </div>

            {/* Required By */}
            <div>
              <label style={labelStyle}>Required By</label>
              <input type="date" style={inputStyle} value={requiredByDate} onChange={e => setRequiredByDate(e.target.value)} />
            </div>

            {/* Description (full width) */}
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Description</label>
              <input style={inputStyle} value={description} onChange={e => setDescription(e.target.value)} placeholder="Brief PO summary" />
            </div>
          </div>
        </div>

        {/* ── Ship-To Card ────────────────────────────────────────────────── */}
        <div style={cardStyle}>
          <div style={sectionTitleStyle}>Ship-To / Delivery</div>
          <div style={{ fontSize: 11, color: LABEL_GRAY, marginBottom: 12, marginTop: -8 }}>
            Auto-filled from job site address. Edit if delivery address differs.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 80px 120px', gap: '14px 16px' }}>
            <div>
              <label style={labelStyle}>Address</label>
              <input style={inputStyle} value={shipAddress} onChange={e => setShipAddress(e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>City</label>
              <input style={inputStyle} value={shipCity} onChange={e => setShipCity(e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>State</label>
              <input style={inputStyle} value={shipState} onChange={e => setShipState(e.target.value)} maxLength={2} />
            </div>
            <div>
              <label style={labelStyle}>ZIP</label>
              <input style={inputStyle} value={shipZip} onChange={e => setShipZip(e.target.value)} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 20px', marginTop: 14 }}>
            <div>
              <label style={labelStyle}>Contact Name</label>
              <input style={inputStyle} value={shipContactName} onChange={e => setShipContactName(e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Contact Phone</label>
              <input style={inputStyle} value={shipContactPhone} onChange={e => setShipContactPhone(e.target.value)} />
            </div>
          </div>
        </div>

        {/* ── Line Items (horizontal table) ──────────────────────────────── */}
        <div style={cardStyle}>
          <div style={{ ...sectionTitleStyle, marginBottom: 10 }}>Line Items</div>

          {/* Table header */}
          <div style={{ display: 'grid', gridTemplateColumns: '36px 2fr 130px 100px 80px 90px 90px 100px 36px', gap: 6, padding: '6px 0', borderBottom: `2px solid ${NAVY}`, marginBottom: 4 }}>
            {['#', 'Description', 'GL Account', 'Job #', 'Mode', 'Qty', 'Unit Price', 'Total', ''].map((h, i) => (
              <div key={i} style={{ fontSize: 10, fontWeight: 700, color: NAVY, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</div>
            ))}
          </div>

          {/* Table rows */}
          {lines.map((line, idx) => {
            const isFlat = line.entry_mode === 'FLAT_TOTAL';
            return (
              <div key={line._key} style={{ display: 'grid', gridTemplateColumns: '36px 2fr 130px 100px 80px 90px 90px 100px 36px', gap: 6, padding: '6px 0', borderBottom: `1px solid #edf2f7`, alignItems: 'center', background: idx % 2 === 0 ? '#fff' : BG_LIGHT }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: LABEL_GRAY, textAlign: 'center' }}>{idx + 1}</div>
                <input style={{ ...inputStyle, fontSize: 12 }} value={line.description} onChange={e => updateLine(line._key, 'description', e.target.value)} placeholder="Item description *" />
                <select style={{ ...selectStyle, fontSize: 12 }} value={line.gl_account || ''} onChange={e => {
                  updateLine(line._key, 'gl_account', e.target.value);
                  const gl = glAccounts.find(g => g.gl_number === e.target.value);
                  if (gl) updateLine(line._key, 'gl_description', gl.description);
                }}>
                  <option value="">—</option>
                  {glAccounts.map(g => <option key={g.gl_number} value={g.gl_number}>{g.gl_number} — {g.description}</option>)}
                </select>
                <select style={{ ...selectStyle, fontSize: 12 }} value={line.job_number || ''} onChange={e => updateLine(line._key, 'job_number', e.target.value)}>
                  <option value="">—</option>
                  {jobs.map(j => <option key={j.job_number} value={j.job_number}>{j.job_number}</option>)}
                </select>
                {/* Mode toggle */}
                <button type="button" onClick={() => toggleEntryMode(line._key)} style={{ fontSize: 10, fontWeight: 600, border: `1px solid ${BORDER}`, borderRadius: 3, padding: '3px 4px', cursor: 'pointer', background: isFlat ? '#edf2f7' : '#fff', color: isFlat ? NAVY : LABEL_GRAY }}>
                  {isFlat ? 'Flat' : 'Qty×$'}
                </button>
                {/* Qty */}
                {isFlat ? (
                  <div style={{ textAlign: 'center', fontSize: 12, color: LABEL_GRAY }}>—</div>
                ) : (
                  <input type="number" min="0" step="any" style={{ ...inputStyle, fontSize: 12, textAlign: 'right' }} value={line.quantity} onChange={e => updateLine(line._key, 'quantity', parseFloat(e.target.value) || 0)} />
                )}
                {/* Unit price */}
                <input type="number" min="0" step="0.01" style={{ ...inputStyle, fontSize: 12, textAlign: 'right' }} value={line.unit_price} onChange={e => updateLine(line._key, 'unit_price', parseFloat(e.target.value) || 0)} placeholder={isFlat ? 'Amount' : 'Price'} />
                {/* Line total */}
                <div style={{ fontSize: 13, fontWeight: 600, color: NAVY, textAlign: 'right', paddingRight: 4 }}>
                  {fmtCurrency(lineAmount(line))}
                </div>
                {/* Remove */}
                {lines.length > 1 ? (
                  <button type="button" onClick={() => removeLine(line._key)} style={{ background: 'transparent', border: 'none', color: RED, fontSize: 16, cursor: 'pointer', fontWeight: 700, lineHeight: 1 }} title="Remove line">✕</button>
                ) : <div />}
              </div>
            );
          })}

          {/* + Add Line */}
          <button
            type="button"
            onClick={addLine}
            style={{ marginTop: 10, width: '100%', padding: '8px 0', fontSize: 13, fontWeight: 600, color: NAVY, border: `2px dashed ${BORDER}`, borderRadius: 4, background: '#fff', cursor: 'pointer' }}
          >
            + Add Line Item
          </button>

          {/* Grand total */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'baseline', marginTop: 16, paddingTop: 12, borderTop: `2px solid ${NAVY}` }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: LABEL_GRAY, marginRight: 16 }}>PO TOTAL</span>
            <span style={{ fontSize: 22, fontWeight: 700, color: NAVY }}>{fmtCurrency(grandTotal)}</span>
          </div>
        </div>

        {/* ── Notes & Approval Card ──────────────────────────────────────── */}
        <div style={cardStyle}>
          <div style={sectionTitleStyle}>Notes & Approval</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 20px' }}>
            <div>
              <label style={labelStyle}>Vendor-Visible Notes</label>
              <textarea
                style={{ ...inputStyle, minHeight: 70, resize: 'vertical' }}
                value={notesVendor}
                onChange={e => setNotesVendor(e.target.value)}
                placeholder="Printed on issued PO — visible to vendor"
              />
              <div style={{ fontSize: 10, color: LABEL_GRAY, marginTop: 2 }}>Printed on the issued PO PDF</div>
            </div>
            <div>
              <label style={labelStyle}>Internal Notes</label>
              <textarea
                style={{ ...inputStyle, minHeight: 70, resize: 'vertical' }}
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Internal only — not visible to vendor"
              />
              <div style={{ fontSize: 10, color: LABEL_GRAY, marginTop: 2 }}>Visible to AP staff only</div>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Approver Email <span style={{ color: RED }}>*</span></label>
              <input type="email" style={inputStyle} value={approverEmail} onChange={e => setApproverEmail(e.target.value)} placeholder="manager@powerlinkonline.com" />
              <div style={{ fontSize: 10, color: LABEL_GRAY, marginTop: 2 }}>This person will receive an email to approve or decline the PO</div>
            </div>
          </div>
        </div>

        {/* ── Error + Actions ─────────────────────────────────────────────── */}
        {error && (
          <div style={{ background: '#FEF2F2', border: `1px solid #FECACA`, borderRadius: 6, padding: '10px 16px', marginBottom: 16, color: RED, fontSize: 13 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={() => navigate('/purchase-orders')}
            style={{ padding: '8px 20px', fontSize: 13, border: `1px solid ${BORDER}`, borderRadius: 4, background: '#fff', color: LABEL_GRAY, cursor: 'pointer' }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            style={{ padding: '8px 28px', fontSize: 13, fontWeight: 600, border: 'none', borderRadius: 4, background: saving ? '#a0aec0' : NAVY, color: '#fff', cursor: saving ? 'default' : 'pointer' }}
          >
            {saving ? 'Saving…' : isEditMode ? 'Save Changes' : 'Create PO'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PORequestForm;
